import classNames from "classnames";
import Layout from "../../components/Layout";
import { Spinner } from "../../components/Spinner";
import formatCurrency from "../../utils/formatCurrency";
import {
  RequoteRequestModel,
  useRequoteInsuranceQuote
} from "./insurance-quote";
import useDebounce from "../../hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "../../components/Dialog";
import useLivlyUser from "../../context/UserProvider";
import { useNavigate, useOutletContext } from "react-router";
import { InsuranceQuoteContext } from "@/components/InsuranceQuoteLayout";
import {
  AssurantQuote,
  AssurantQuoteV2,
  DisclosureType,
  SelectionMetaData
} from "@/types/Insurance";
import {
  useDeleteInsuranceV2Quote,
  useRequoteInsuranceV2Quote,
  useUpdateInsuranceV2Quote
} from "./insurance-quote-v2";
import Alert from "@/components/Alert";
import { User } from "@/types/User";
import { useGetDisclosures } from "./checkout-insurance";

function getValue(
  data: { name: string; value: any }[] = [],
  paramName: string
): any {
  return data.find((p) => p.name === paramName)?.value;
}

function InsuranceCoveragesPage() {
  const { user } = useLivlyUser();
  const {
    data,
    refetch,
    isAssurantV2,
    selectionMetaData,
    updateMetaData,
    isRequire300kLiability
  } = useOutletContext<InsuranceQuoteContext>();

  return (
    <div>
      {isAssurantV2 ? (
        <InsuranceCoveragesV2
          data={data as AssurantQuoteV2}
          user={user}
          refetch={refetch}
          selectionMetaData={selectionMetaData}
          updateMetaData={updateMetaData}
          isRequire300kLiability={isRequire300kLiability}
        />
      ) : (
        <InsuranceCoveragesV1
          data={data as AssurantQuote}
          user={user}
          refetch={refetch}
        />
      )}
    </div>
  );
}

const InsuranceCoveragesV1 = ({
  data,
  user,
  refetch
}: {
  data: AssurantQuote;
  user: User;
  refetch: () => void;
}) => {
  const requoteMutation = useRequoteInsuranceQuote(user);

  const {
    schedules,
    personalLiabilityAllowed,
    liabilityAmount,
    personalPropertyConfig,
    deductibleAllowed,
    deductible,
    riskState,
    hurricaneDeductible,
    coverageSewerBackup,
    getCoveragePersonalProperty
  } = useMemo(() => {
    if (!data) return {} as any;

    const v1 = data as AssurantQuote;
    return {
      schedules: v1.purchase.schedules,
      personalLiabilityAllowed: v1.configuration.personalLiabilityAllowed,
      liabilityAmount: v1.presentation.coverageLiability,
      personalPropertyConfig: {
        step: v1.configuration.personalPropertyIncr,
        min: v1.configuration.personalPropertyMin,
        max: v1.configuration.personalPropertyMax
      },
      deductibleAllowed: v1.configuration.deductibleAllowed,
      deductible: v1.presentation.deductible,
      riskState: v1.presentation.riskState,
      hurricaneDeductible: v1.presentation.hurricaneDeductible,
      coverageSewerBackup: v1.presentation.coverageSewerBackup,
      getCoveragePersonalProperty: v1.presentation.coveragePersonalProperty
    };
  }, [data]);

  const [coverageOptionType, setCoverageOptionType] =
    useState<CoverageOptionType | null>(null);
  const [coveragePersonalProperty, setCoveragePersonalProperty] = useState(
    getCoveragePersonalProperty ?? ""
  );

  const fullPlan = schedules?.find?.((s: any) => s.numberOfPayments === 1);
  const monthlyPlan = schedules?.find?.((s: any) => s.numberOfPayments > 6);

  const onChange = async (coverage: Partial<RequoteRequestModel>) => {
    try {
      const v1 = data as AssurantQuote;
      const request: RequoteRequestModel = {
        coveragePersonalLiability: v1.presentation.coverageLiability ?? "",
        coveragePersonalProperty:
          v1.presentation.coveragePersonalProperty ?? "",
        deductable: v1.presentation.deductible ?? "",
        policyEffectiveDate: v1.configuration.policyEffectiveDate ?? "",
        coverageSewerBackup: v1.presentation.coverageSewerBackup ?? "",
        ...coverage
      };
      await requoteMutation.mutateAsync(request);

      refetch();
    } catch (e) {}
  };

  let debouncedCoveragePersonalProperty = useDebounce(
    String(coveragePersonalProperty),
    1000
  );

  useEffect(() => {
    if (!data) return;
    onChange({
      coveragePersonalProperty: debouncedCoveragePersonalProperty
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCoveragePersonalProperty]);

  useEffect(() => {
    if (data && coveragePersonalProperty === "") {
      setCoveragePersonalProperty(String(getCoveragePersonalProperty));
    }
  }, [data]);

  return (
    <Layout
      title="Edit Coverage"
      back={{
        label: "Insurance",
        to: `../insurance-quote`
      }}
    >
      {requoteMutation.isLoading ? (
        <div className="fixed inset-0 bg-gray-100 bg-opacity-50 flex flex-col items-center justify-center md:ml-64">
          <Spinner size="xl" color="livly" />
          <p>Calculating premiums. Please wait...</p>
        </div>
      ) : null}
      <div className="max-w-4xl mx-auto">
        <div className="flex divide-x divide-gray-200 border-b border-gray-200 pb-4">
          <CoveragePlan
            label="Full Term"
            icon="sack-dollar"
            amount={fullPlan?.downPaymentAmount}
          />

          <CoveragePlan
            label="Monthly"
            icon="hand-holding-dollar"
            amount={
              Number(monthlyPlan?.installmentAmount) +
              Number(monthlyPlan?.serviceFee)
            }
          />
        </div>

        <CoverageSection
          label="Personal Liability"
          optionType={CoverageOptionType.Liability}
          allowed={personalLiabilityAllowed}
          selected={liabilityAmount}
          onChange={onChange}
          setCoverageOptionType={setCoverageOptionType}
          isAssurantV2={false}
        />
        <div className="mt-8">
          <div className="flex items-center gap-2">
            <p className="font-medium text-lg">Personal Belongings</p>
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 hover:bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() =>
                setCoverageOptionType(CoverageOptionType.PersonalBelongings)
              }
            >
              <FontAwesomeIcon icon={["fal", "info-circle"]} />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-4">
            <input
              type="range"
              className="accent-indigo-600"
              step={personalPropertyConfig?.step}
              min={personalPropertyConfig?.min}
              max={personalPropertyConfig?.max}
              value={Number(coveragePersonalProperty)}
              onChange={(e) => setCoveragePersonalProperty(e.target.value)}
            />
            <p>{formatCurrency(Number(coveragePersonalProperty))}</p>
          </div>
        </div>
        <CoverageSection
          label="Deductible"
          optionType={CoverageOptionType.Deductible}
          allowed={deductibleAllowed}
          selected={deductible}
          onChange={onChange}
          setCoverageOptionType={setCoverageOptionType}
          isAssurantV2={false}
        />
        {hurricaneDeductible ? (
          <div className="mt-8">
            <p className="font-medium text-lg">Hurricane Deductible</p>
            <div className="flex gap-2 mt-2">
              <span>{formatCurrency(Number(hurricaneDeductible))}</span>
            </div>
          </div>
        ) : null}
        {riskState === "NY" ? (
          <CoverageStatic label="Flood" optionType={CoverageOptionType.Flood} />
        ) : null}
        {riskState === "IL" ? (
          <CoverageSewerBackup
            selected={coverageSewerBackup}
            onChange={onChange}
            setCoverageOptionType={setCoverageOptionType}
            isAssurantV2={false}
          />
        ) : null}
      </div>
      <InformationModal
        coverageOptionType={coverageOptionType}
        onClose={() => setCoverageOptionType(null)}
      />
    </Layout>
  );
};

const InsuranceCoveragesV2 = ({
  data,
  user,
  refetch,
  selectionMetaData,
  updateMetaData,
  isRequire300kLiability
}: {
  data: AssurantQuoteV2;
  user: User;
  refetch: () => void;
  selectionMetaData: SelectionMetaData | null;
  updateMetaData: (newMetaData: SelectionMetaData) => void;
  isRequire300kLiability: boolean;
}) => {
  const navigate = useNavigate();
  const requoteMutationV2 = useRequoteInsuranceV2Quote(user);
  const updateAssurantQuoteMutation = useUpdateInsuranceV2Quote(user);
  const deleteAssuranceQuoteMutation = useDeleteInsuranceV2Quote(user);

  // Memoize data extraction for performance and clarity
  const {
    schedules,
    personalLiabilityAllowed,
    liabilityAmount,
    personalPropertyConfig,
    deductibleAllowed,
    deductible,
    riskState,
    hurricaneDeductible,
    coverageSewerBackup,
    fraudExpenseProtection,
    earthquake,
    getCoveragePersonalProperty
  } = useMemo(() => {
    if (!data) return {} as any;
    const v2 = data as AssurantQuoteV2;
    return {
      schedules: v2.quoteResponse.transactionDetails.payments.schedules,
      personalLiabilityAllowed: getValue(
        v2.quoteResponse.coveragesCue.parameters,
        "personalLiabilityOptions"
      ),
      liabilityAmount: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "liabilityAmount"
      ),
      personalPropertyConfig: {
        step: getValue(
          v2.quoteResponse.coveragesCue.parameters,
          "personalPropertyIncrement"
        ),
        min: getValue(
          v2.quoteResponse.coveragesCue.parameters,
          "personalPropertyMin"
        ),
        max: getValue(
          v2.quoteResponse.coveragesCue.parameters,
          "personalPropertyMax"
        )
      },
      deductibleAllowed: getValue(
        v2.quoteResponse.coveragesCue.parameters,
        "deductibleOptions"
      ),
      deductible: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "deductible"
      ),
      riskState: v2.quoteResponse.policyHolderDetails?.insured?.address?.state,
      hurricaneDeductible: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "hurricaneDeductible"
      ),
      coverageSewerBackup: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "waterBackup"
      ),
      fraudExpenseProtection: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "identityFraud"
      ),
      earthquake: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "earthquake"
      ),
      getCoveragePersonalProperty: getValue(
        v2.quoteResponse.coverageDetails.parameters,
        "personalPropertyAmount"
      )
    };
  }, [data]);

  const [coverageOptionType, setCoverageOptionType] =
    useState<CoverageOptionType | null>(null);
  const [coveragePersonalProperty, setCoveragePersonalProperty] = useState(
    getCoveragePersonalProperty ?? ""
  );

  const fullPlan = schedules?.find?.((s: any) => s.numberOfPayments === 1);
  const quarterlyPlan = schedules?.find?.((s: any) => s.numberOfPayments === 4);
  const monthlyPlan = schedules?.find?.((s: any) => s.numberOfPayments > 6);

  const onChange = async (coverage: Partial<RequoteRequestModel>) => {
    try {
      const v2 = (data as AssurantQuoteV2).quoteResponse;
      const quoteRequest = (data as AssurantQuoteV2).quoteRequest;
      const paramNameMap: Record<string, string> = {
        iuiTypes: "iuiType"
      };
      const updatedParameters = v2.coverageDetails.parameters.map((param) => {
        // Map parameter name if mapping exists, otherwise use original
        const mappedName = paramNameMap[param.name] || param.name;
        // If the coverage object contains a value for this parameter, update it
        if (
          coverage &&
          Object.prototype.hasOwnProperty.call(coverage, param.name)
        ) {
          return {
            ...param,
            name: mappedName,
            value: (coverage as any)[param.name]
          };
        }
        // If just the name needs to be changed (iuiTypes to iuiType)
        if (param.name === "iuiTypes") {
          return {
            ...param,
            name: "iuiType"
          };
        }
        return param;
      });
      const requestV2 = {
        transaction: {
          clientId: v2.transactionDetails.clientId,
          productType: v2.transactionDetails.productId,
          workItemId: v2.transactionDetails.workItemId,
          jobNumber: quoteRequest.transaction.jobNumber,
          policyEffectiveDate: v2.transactionDetails.policyEffectiveDate,
          referralId: v2.transactionDetails.referralId
        },
        policyHolder: {
          insured: quoteRequest.policyHolder.insured
        },
        underWriting: {
          questions: quoteRequest.underWriting.questions
        },
        coverage: {
          parameters: updatedParameters
        }
      };
      await requoteMutationV2.mutateAsync(requestV2);
      if (data?.selectionMetaData?.isPlanAdded) {
        updateMetaData({ ...data?.selectionMetaData, isPlanAdded: false });
        await updateAssurantQuoteMutation.mutateAsync({
          newRequest: null,
          SelectionMetaData: {
            ...selectionMetaData,
            isPlanAdded: false
          }
        });
      }
      refetch();
    } catch (e) {}
  };

  useEffect(() => {
    if (data && coveragePersonalProperty === "") {
      setCoveragePersonalProperty(String(getCoveragePersonalProperty));
    }
  }, [data]);

  let debouncedCoveragePersonalProperty = useDebounce(
    String(coveragePersonalProperty),
    1000
  );

  // Update coveragePersonalProperty in the quote if it has changed
  useEffect(() => {
    if (!data) return;
    if (
      debouncedCoveragePersonalProperty !== String(getCoveragePersonalProperty)
    ) {
      onChange({
        //@ts-ignore
        personalPropertyAmount: debouncedCoveragePersonalProperty
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCoveragePersonalProperty, getCoveragePersonalProperty]);

  const handleGetFreshQuote = async () => {
    await deleteAssuranceQuoteMutation.mutateAsync();
    const quote = data as AssurantQuoteV2;
    await updateAssurantQuoteMutation.mutateAsync(
      {
        newRequest: {
          ...quote.quoteRequest,
          transaction: {
            ...quote.quoteRequest.transaction,
            referralId: Math.floor(
              1000000000 + Math.random() * 9000000000
            ).toString(), // 10-digit random
            workItemId: null
          }
        },
        SelectionMetaData: { ...selectionMetaData, isPlanAdded: false }
      },
      {
        onSuccess: () => {
          refetch();
          navigate("../insurance-quote");
        }
      }
    );
  };

  return (
    <Layout
      title="Edit Coverage"
      back={{
        label: "Insurance",
        to: `../insurance-quote`
      }}
    >
      {requoteMutationV2.isLoading ||
      updateAssurantQuoteMutation.isLoading ||
      deleteAssuranceQuoteMutation?.isLoading ? (
        <div className="fixed inset-0 bg-gray-100 bg-opacity-50 flex flex-col items-center justify-center md:ml-64">
          <Spinner size="xl" color="livly" />
          <p>Calculating premiums. Please wait...</p>
        </div>
      ) : null}
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row  divide-y md:divide-x divide-gray-200 border-b border-gray-200 pb-4">
          <CoveragePlan
            label="Full Term"
            icon="sack-dollar"
            amount={fullPlan?.downPaymentAmount}
          />

          <CoveragePlan
            label="Quarterly"
            icon="badge-dollar"
            amount={
              Number(quarterlyPlan?.installmentAmount) +
              Number(quarterlyPlan?.serviceFee)
            }
          />

          <CoveragePlan
            label="Monthly"
            icon="hand-holding-dollar"
            amount={
              Number(monthlyPlan?.installmentAmount) +
              Number(monthlyPlan?.serviceFee)
            }
          />
        </div>
        {requoteMutationV2.data?.quoteResponse.transactionDetails
          .responseMessage && (
          <Alert
            message={
              requoteMutationV2.data?.quoteResponse.transactionDetails
                .responseMessage
            }
            action={{
              label: "Get a Fresh Quote",
              onClick: () => handleGetFreshQuote()
            }}
          />
        )}
        <div className="mt-2">
          <p className="font-medium text-xs text-gray-600">
            * Payment plan options are available for all payment methods. If you
            choose an installment payment plan, an installment/service fee of $1
            to $5 will apply.
          </p>
        </div>
        <CoverageSection
          label="Personal Liability"
          optionType={CoverageOptionType.Liability}
          allowed={personalLiabilityAllowed}
          selected={liabilityAmount}
          onChange={onChange}
          setCoverageOptionType={setCoverageOptionType}
          isAssurantV2={true}
          isRequire300kLiability={isRequire300kLiability}
        />
        <div className="mt-8">
          <div className="flex items-center gap-2">
            <p className="font-medium text-lg">Personal Belongings</p>
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 hover:bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() =>
                setCoverageOptionType(CoverageOptionType.PersonalBelongings)
              }
            >
              <FontAwesomeIcon icon={["fal", "info-circle"]} />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-4">
            <input
              type="range"
              className="accent-indigo-600"
              step={personalPropertyConfig?.step}
              min={personalPropertyConfig?.min}
              max={personalPropertyConfig?.max}
              value={Number(coveragePersonalProperty)}
              onChange={(e) => setCoveragePersonalProperty(e.target.value)}
            />
            <p>{formatCurrency(Number(coveragePersonalProperty))}</p>
          </div>
        </div>
        <CoverageSection
          label="Deductible"
          optionType={CoverageOptionType.Deductible}
          allowed={deductibleAllowed}
          selected={deductible}
          onChange={onChange}
          setCoverageOptionType={setCoverageOptionType}
          isAssurantV2={true}
        />
        {hurricaneDeductible && riskState === "FL" ? (
          <CoverageHurricane
            selected={hurricaneDeductible}
            onChange={onChange}
          />
        ) : null}
        {riskState === "NY" ? (
          <CoverageStatic label="Flood" optionType={CoverageOptionType.Flood} />
        ) : null}

        <CoverageStatic label="Replacement Coverage" />

        <CoverageSewerBackup
          selected={coverageSewerBackup}
          onChange={onChange}
          setCoverageOptionType={setCoverageOptionType}
          isAssurantV2={true}
        />

        <CoverageFraudExpense
          selected={fraudExpenseProtection}
          onChange={onChange}
          setCoverageOptionType={setCoverageOptionType}
        />

        {riskState === "WA" || riskState === "CA" ? (
          <CoverageEarthquake
            selected={earthquake}
            onChange={onChange}
            setCoverageOptionType={setCoverageOptionType}
          />
        ) : null}
      </div>
      <InformationModalV2
        coverageOptionType={coverageOptionType}
        onClose={() => setCoverageOptionType(null)}
        userState={user.state}
      />
    </Layout>
  );
};

// --- Subcomponents for clarity and DRY ---

function CoveragePlan({ label, icon, amount }: any) {
  return (
    <div className="flex-1 p-4">
      <FontAwesomeIcon icon={icon} className="text-red-400 h-10 w-10" />
      <p className="mt-4 text-gray-700 uppercase font-medium">{label}</p>
      <p className="text-4xl font-light mt-2">
        {amount !== undefined
          ? formatCurrency(Number(amount)).replace(".00", "")
          : "$0"}
      </p>
    </div>
  );
}

function CoverageSection({
  label,
  optionType,
  allowed,
  selected,
  onChange,
  setCoverageOptionType,
  isAssurantV2,
  isRequire300kLiability = false
}: any) {
  if (!allowed) return null;
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <p className="font-medium text-lg">{label}</p>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 hover:bg-slate-50 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          onClick={() => setCoverageOptionType(optionType)}
        >
          <FontAwesomeIcon icon={["fal", "info-circle"]} />
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        {(Array.isArray(allowed) ? allowed : [])
          .filter((amount) =>
            isRequire300kLiability ? amount === 300000 : true
          )
          .map((amount) => (
            <button
              key={amount}
              type="button"
              className={classNames(
                {
                  "border-red-400": selected === amount.toString()
                },
                "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
              )}
              onClick={() =>
                onChange({
                  [optionType === CoverageOptionType.Deductible
                    ? `${isAssurantV2 ? "deductible" : "deductable"}`
                    : `${
                        isAssurantV2
                          ? "liabilityAmount"
                          : "coveragePersonalLiability"
                      }`]: amount.toString()
                })
              }
            >
              {formatCurrency(amount)}
            </button>
          ))}
      </div>
    </div>
  );
}

function CoverageSewerBackup({
  selected,
  onChange,
  setCoverageOptionType,
  isAssurantV2
}: any) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <p className="font-medium text-lg">Water/Sewer Back Up</p>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 hover:bg-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          onClick={() => setCoverageOptionType(CoverageOptionType.SewerBackUp)}
        >
          <FontAwesomeIcon icon={["fal", "info-circle"]} />
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected === "Y" },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
          onClick={() =>
            onChange({
              [!isAssurantV2 ? "coverageSewerBackup" : "waterBackup"]: "Y"
            })
          }
        >
          Included
        </button>
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected === "N" },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
          onClick={() =>
            onChange({
              [!isAssurantV2 ? "coverageSewerBackup" : "waterBackup"]: "N"
            })
          }
        >
          Removed
        </button>
      </div>
    </div>
  );
}

function CoverageFraudExpense({
  selected,
  onChange,
  setCoverageOptionType
}: any) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <p className="font-medium text-lg">Fraud Expense Protection</p>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 hover:bg-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          onClick={() =>
            setCoverageOptionType(CoverageOptionType.FraudExpenseProtection)
          }
        >
          <FontAwesomeIcon icon={["fal", "info-circle"]} />
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected === "Y" },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
          onClick={() => onChange({ identityFraud: "Y" })}
        >
          Included
        </button>
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected === "N" },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
          onClick={() => onChange({ identityFraud: "N" })}
        >
          Removed
        </button>
      </div>
    </div>
  );
}

function CoverageEarthquake({
  selected,
  onChange,
  setCoverageOptionType
}: any) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <p className="font-medium text-lg">Earthquake</p>
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 hover:bg-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          onClick={() => setCoverageOptionType(CoverageOptionType.Earthquake)}
        >
          <FontAwesomeIcon icon={["fal", "info-circle"]} />
        </button>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected === "Y" },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
          onClick={() => onChange({ earthquake: "Y" })}
        >
          Included
        </button>
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected === "N" },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
          onClick={() => onChange({ earthquake: "N" })}
        >
          Removed
        </button>
      </div>
    </div>
  );
}

function CoverageHurricane({ selected, onChange }: any) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <p className="font-medium text-lg">Hurricane Deductible</p>
      </div>
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          className={classNames(
            { "border-red-400": selected !== "0" && selected !== 0 },
            "px-3 py-2 border rounded-md hover:bg-slate-50 transition duration-300"
          )}
        >
          Included ({formatCurrency(Number(selected))})
        </button>
      </div>
    </div>
  );
}

function CoverageStatic({ label, optionType }: any) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2">
        <p className="font-medium text-lg">{label}</p>
        {optionType && (
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 hover:bg-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white "
            onClick={() => {}}
          >
            <FontAwesomeIcon icon={["fal", "info-circle"]} />
          </button>
        )}
      </div>
      <div className="flex gap-2 mt-2 border border-red-400 w-fit p-2 rounded">
        <span>Included</span>
      </div>
    </div>
  );
}

function InformationModal({
  coverageOptionType,
  onClose
}: {
  coverageOptionType: CoverageOptionType | null;
  onClose: () => void;
}) {
  return (
    <Modal
      open={coverageOptionType != null}
      onClose={onClose}
      title={coverageOptionType ?? ""}
    >
      <div className="mt-4">
        {coverageOptionType && CoverageOptionDescriptions[coverageOptionType]}
      </div>
    </Modal>
  );
}

// --- New InformationModalV2 for dynamic disclosure fetching ---

function InformationModalV2({
  coverageOptionType,
  onClose,
  userState
}: {
  coverageOptionType: CoverageOptionType | null;
  onClose: () => void;
  userState: string;
}) {
  const isSewerBackup = coverageOptionType === CoverageOptionType.SewerBackUp;

  const disclosureType = isSewerBackup
    ? DisclosureType.WaterSewerBackup
    : undefined;

  const {
    data: disclosureData,
    isLoading,
    isError
  } = useGetDisclosures(disclosureType, userState);

  const content = useMemo(() => {
    if (!coverageOptionType) return null;

    if (isSewerBackup) {
      if (isLoading) {
        return (
          <div className="text-center text-gray-500">Loading disclosure...</div>
        );
      }
      if (isError) {
        return (
          <div className="text-center text-red-500">
            Failed to load disclosure.
          </div>
        );
      }
      if (disclosureData) {
        return (
          <div
            className="text-sm"
            dangerouslySetInnerHTML={{ __html: disclosureData.Content }}
          />
        );
      }
    }

    return CoverageOptionDescriptions[coverageOptionType] ?? null;
  }, [coverageOptionType, isSewerBackup, isLoading, isError, disclosureData]);

  return (
    <Modal
      open={!!coverageOptionType}
      onClose={onClose}
      title={coverageOptionType ?? ""}
    >
      <div className="mt-4">{content}</div>
    </Modal>
  );
}

export enum CoverageOptionType {
  Deductible = "Deductible",
  Liability = "Personal Liability",
  PersonalBelongings = "Personal Belongings",
  Flood = "Flood",
  SewerBackUp = "Sewer Backup",
  FraudExpenseProtection = "Fraud Expense Protection",
  Earthquake = "Earthquake",
  Hurricane = "Hurricane Deductible"
}

const CoverageOptionDescriptions: Record<CoverageOptionType, any> = {
  Deductible: (
    <p className="text-sm">
      Your deductible is how much you’re responsible for paying before your
      policy steps in to help. It’s deducted from your personal belongings
      claims made. A higher deductible will lower your monthly payment but that
      means you’ll need to pay more out of pocket if you file a claim.
    </p>
  ),
  "Personal Liability": (
    <p className="text-sm">
      Liability coverage protects you in case you’re held responsible for an
      accident that causes injury or property damage. If someone gets hurt,
      there’s damage to your unit or someone’s stuff gets ruined, you’ll be
      protected up to the coverage amount you choose.
    </p>
  ),
  "Personal Belongings": (
    <p className="text-sm">
      Personal belongings coverage protects your stuff, like your furniture,
      clothing, electronics and other personal items. Your valuables are
      protected whether they’re damaged or stolen, at your home or anywhere else
      in the world.
    </p>
  ),
  Flood: (
    <div className="text-sm">
      <p>
        Flood coverage <strong>protects your belongings</strong> if your
        building is suddenly damaged from flooding events, like an overflow of a
        body of water, storm surges, or tidal waves.
      </p>
      <p>The coverage will protect you for the following:</p>
      <ul>
        <li>Pays up to $2,500 if your personal belongings are damaged</li>
        <li>Pays up to $1,000 in additional living expenses</li>
        <li>
          If due to a flood, your whole building needs to be evacuated or you
          are not able to get to your unit and need to stay somewhere else
        </li>
        <li>Pays up to $100 in Food Spoilage</li>
        <li>
          Flood events sometimes causes power outages so this will cover your
          food that spoils in your refrigerator or freezer.
        </li>
      </ul>
      <p>
        You are making a great decision by purchasing this policy from Assurant,{" "}
        <strong>
          the ONLY company within the multifamily housing industry to offer this
          coverage as part of a renters insurance program!
        </strong>
      </p>
    </div>
  ),
  "Sewer Backup": (
    <div className="text-sm">
      <p className="font-bold">IMPORTANT NOTICE</p>
      <p className="font-semibold mt-1">
        Water Backup and Sump Overflow Coverage (Illinois)
      </p>
      <p className="mt-2">
        Coverage includes up to $2,500 in damages if a sewer or drain backs up
        into your home. A $250 deductible applies.
      </p>
    </div>
  ),
  "Fraud Expense Protection": (
    <div className="text-sm">
      <p className="font-bold">IMPORTANT NOTICE</p>
      <p className="font-semibold mt-1">Fraud Expense Protection</p>
      <p className="mt-2">
        Identity fraud expense coverage includes up to $15,000 for your expenses
        if you’re a victim of identify fraud. A $100 deductible applies.
      </p>
    </div>
  ),
  Earthquake: (
    <div className="text-sm">
      <p className="font-bold">IMPORTANT NOTICE</p>
      <p className="font-semibold mt-1">
        Earthquake Coverage (only in WA and CA)
      </p>
      <p className="mt-2">
        Coverage includes up to the amount you chose for your belongings and
        additional living expenses if you need to stay somewhere else. A
        deductible applies.{" "}
      </p>
    </div>
  ),
  "Hurricane Deductible": (
    <div className="text-sm">
      <p className="font-bold">IMPORTANT NOTICE</p>
      <p className="font-semibold mt-1">Hurricane Deductible</p>
      <p className="mt-2">
        This is the amount you must pay out of pocket for covered
        hurricane-related losses before your insurance coverage applies. Please
        review your policy for details and state-specific requirements.
      </p>
    </div>
  )
};

export { InsuranceCoveragesPage };
